import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { getFormattedValue, getUnformattedValue } from '../utils/formatting';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
const propTypes = {
  /**
   * Input value text alignment
   */
  align: PropTypes.oneOf(['left', 'right']),
  /**
   * Allow decimals or whole dollar input only
   */
  allowDecimals: PropTypes.bool,
  /**
   * The currency symbol to display
   */
  currencySymbol: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * **DEPRECATED** - use `onBlur` instead!<br />
   * Callback function for the input blur event<br />
   * `(unformattedValue, formattedValue) => void`
   **/
  handleBlur: PropTypes.func,
  /**
   * **DEPRECATED** - use `onChange` instead!<br />
   * Callback function for the input value change event<br />
   * `(unformattedValue, formattedValue) => void`
   **/
  handleChange: PropTypes.func,
  /**
   * Enables the input error state
   */
  hasError: PropTypes.bool,
  /**
   * Visually hide the input's label element
   */
  hideLabel: PropTypes.bool,
  /**
   * The input element's id
   **/
  id: PropTypes.string.isRequired,
  /**
   * Content to display in the info popover.
   * Enables the FormLabelPopover component when provided.
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the info popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Initial unformatted input value for the uncontrolled version
   **/
  initialValue: PropTypes.string,
  /**
   * Additional classes for the input element
   */
  inputClass: PropTypes.string,
  /**
   * Flag to indicate whether this input is a required field
   */
  isRequired: PropTypes.bool,
  /**
   * Label for the input
   **/
  label: PropTypes.string.isRequired,
  /**
   * Callback function for the input blur event<br />
   * `(event, unformattedValue, formattedValue, numberValue) => void`
   **/
  onBlur: PropTypes.func,
  /**
   * Callback function for the input change event<br />
   * `(event, unformattedValue, formattedValue, numberValue) => void`
   **/
  onChange: PropTypes.func,
  /**
   * Callback function for the input focus event<br />
   * `(event) => void`
   **/
  onFocus: PropTypes.func,
  /**
   * Appended to the text label when the input is not required.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * Placeholder text for the input
   **/
  placeholder: PropTypes.string,
  /**
   * QA data attribute for automated testing
   **/
  qaId: PropTypes.string,
  /**
   * Enable the selection of the input value on focus
   */
  selectOnFocus: PropTypes.bool,
  /**
   * Append an asterisk to the label when the input is required
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Additional description text to render below the input
   */
  subtext: PropTypes.string,
  /**
   * Input value for controlled inputs. Use `initialValue` for uncontrolled inputs.
   * Controlled inputs require an `onChange` handler to update the value and
   * `onBlur` handler to format the value.
   **/
  value: PropTypes.string
};
function CurrencyInput(_ref) {
  let {
    align = 'right',
    allowDecimals = true,
    currencySymbol = '$',
    errorLevel = 'error',
    errorMessage = '',
    handleBlur,
    handleChange,
    hasError = false,
    hideLabel = false,
    id,
    infoPopoverContent = null,
    infoPopoverProps = {},
    initialValue = '',
    inputClass = '',
    isRequired = false,
    label,
    onBlur,
    onChange,
    onFocus,
    optionalInputLabel = '(optional)',
    placeholder = '$0.00',
    qaId = '',
    selectOnFocus = true,
    showRequiredAsterisk = false,
    subtext,
    value,
    ...inputProps
  } = _ref;
  const [inputValue, setInputValue] = useState(getFormattedValue(initialValue, {
    allowDecimals,
    currencySymbol
  }));
  const [cursorPosition, setCursorPosition] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => {
    const inputEl = inputRef?.current;
    if (inputEl && cursorPosition !== null) {
      // set the cursor position
      inputEl.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [cursorPosition, inputRef]);
  const isControlledInput = typeof value === 'string';
  const onChangeHandler = e => {
    const currentValue = e.target.value;
    const unformattedValue = getUnformattedValue(currentValue, {
      allowDecimals
    });
    const formattedValue = getFormattedValue(unformattedValue, {
      allowDecimals,
      currencySymbol
    });
    const numberValue = unformattedValue ? parseFloat(unformattedValue) : null;
    if (!isControlledInput) {
      setInputValue(currencySymbol + unformattedValue);
    }
    let commaCount = 0;
    if (!currentValue.includes(currencySymbol)) commaCount--;
    for (let i = 0; i < e.target.selectionStart - 1; i++) {
      if (currentValue[i] === ',') commaCount++;
    }
    const newCursor = e.target.selectionStart - commaCount;
    setCursorPosition(newCursor);
    if (typeof handleChange === 'function') {
      handleChange(unformattedValue, formattedValue);
    }
    if (typeof onChange === 'function') {
      onChange(e, unformattedValue, formattedValue, numberValue);
    }
  };
  const onBlurHandler = e => {
    const currentValue = e.target.value;
    const unformattedValue = getUnformattedValue(currentValue, {
      allowDecimals
    });
    const formattedValue = getFormattedValue(unformattedValue, {
      allowDecimals,
      currencySymbol
    });
    const numberValue = unformattedValue ? parseFloat(unformattedValue) : null;
    if (!isControlledInput) {
      setInputValue(formattedValue);
    }
    if (handleBlur) {
      handleBlur(unformattedValue, formattedValue);
    }
    if (typeof onBlur === 'function') {
      onBlur(e, unformattedValue, formattedValue, numberValue);
    }
  };
  const onFocusHandler = event => {
    if (selectOnFocus) {
      const inputEl = inputRef?.current;
      if (inputEl) {
        // select the input value, excluding the dollar sign
        inputEl.setSelectionRange(1, inputEl.value.length);
      }
    }
    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  };
  const wrapperClassNames = cx('asc-currency-input', {
    [`asc-form-input--${errorLevel}`]: hasError
  });
  const inputClassNames = cx('asc-form-input', inputClass, {
    'asc-text--right': align === 'right',
    'asc-text--left': align === 'left'
  });
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  const ariaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
  if (inputProps['aria-describedby']) {
    ariaDescribedByIds.push(inputProps['aria-describedby']);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClassNames,
    "data-qa": qaId
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    hideLabel: hideLabel,
    inputId: id,
    isRequired: isRequired,
    label: label,
    optionalInputLabel: optionalInputLabel,
    showRequiredAsterisk: showRequiredAsterisk
  }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  }), /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    id: id,
    name: id,
    type: "text",
    className: inputClassNames,
    value: isControlledInput ? value : inputValue,
    onChange: onChangeHandler,
    onBlur: onBlurHandler,
    onFocus: onFocusHandler,
    placeholder: placeholder,
    required: isRequired,
    inputMode: "numeric",
    autoComplete: "off",
    ref: inputRef,
    "aria-describedby": ariaDescribedByIds.length ? ariaDescribedByIds.join(' ') : null
  })), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext"
  }, subtext));
}
CurrencyInput.propTypes = propTypes;
export default CurrencyInput;